// src/pages/common/reports/R0006/components/R0006LaborHoursTable.js

import React, { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Typography, Box, Tooltip } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

/**
 * Labor Hours Management Table Component for R0006
 * Uses Tanstack Table v8 with standard HTML table elements
 */
export default function R0006LaborHoursTable({ data, dataReady, isAreasView, selectedArea, currentView }) {
  // Column definitions for the dual-structure table
  const columns = useMemo(
    () => [
      {
        accessorKey: 'restaurant',
        header: 'Restaurant',
        cell: ({ getValue }) => (
          <span style={{ fontWeight: '500' }}>
            {getValue()}
          </span>
        ),
      },
      // FOH Columns
      {
        id: 'fohSchedVsModel',
        header: 'FOH: Sched vs Model',
        accessorKey: 'fohSchedVsModel',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const isTotal = row.original.restaurant === 'TOTALS';
          const schedHours = row.original.fohSchedHours;
          const theoHours = row.original.fohTheoHours;
          const trainingHours = row.original.fohTrainingHours;

          const displayValue = value === 0 ? '+0' : `${value > 0 ? '+' : ''}${value}`;
          const textStyle = {
            fontWeight: '500',
            color: value === 0 ? 'inherit' : (value >= 0 ? '#2e7d32' : '#d32f2f')
          };

          // Show tooltip for restaurant rows and area rows (when Area Summary is NOT selected), but not totals
          if (!isTotal && (schedHours !== null && schedHours !== undefined) && (theoHours !== null && theoHours !== undefined) && selectedArea !== 'Area Summary') {
            return (
              <Tooltip
                title={
                  <div>
                    <div>Sched FOH: {(Number(schedHours) || 0).toFixed(2)}</div>
                    <div>Training FOH: {(Number(trainingHours) || 0).toFixed(2)}</div>
                    <div>Theo FOH: {(Number(theoHours) || 0).toFixed(2)}</div>
                  </div>
                }
                placement="top"
                arrow
              >
                <div style={{ ...textStyle, cursor: 'help', textAlign: 'center' }}>
                  {displayValue}
                </div>
              </Tooltip>
            );
          }

          return <div style={{ ...textStyle, textAlign: 'center' }}>{displayValue}</div>;
        },
      },
      {
        id: 'fohChangeDuringDay',
        header: 'FOH: Change During Day',
        accessorKey: 'fohChangeDuringDay',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const isTotal = row.original.restaurant === 'TOTALS';
          const actualHours = row.original.fohActualHours;
          const schedHours = row.original.fohSchedHours;
          
          const displayValue = value === 0 ? '+0' : `${value > 0 ? '+' : ''}${value}`;
          const textStyle = {
            fontWeight: '500',
            color: value === 0 ? 'inherit' : (value >= 0 ? '#2e7d32' : '#d32f2f')
          };
          
          // Show tooltip for restaurant rows and area rows (when Area Summary is NOT selected), but not totals
          if (!isTotal && (actualHours !== null && actualHours !== undefined) && (schedHours !== null && schedHours !== undefined) && selectedArea !== 'Area Summary') {
            return (
              <Tooltip 
                title={
                  <div>
                    <div>Actual FOH: {(Number(actualHours) || 0).toFixed(2)}</div>
                    <div>Sched FOH: {(Number(schedHours) || 0).toFixed(2)}</div>
                  </div>
                }
                placement="top"
                arrow
              >
                <div style={{ ...textStyle, cursor: 'help', textAlign: 'center' }}>
                  {displayValue}
                </div>
              </Tooltip>
            );
          }
          
          return <div style={{ ...textStyle, textAlign: 'center' }}>{displayValue}</div>;
        },
      },
      {
        id: 'fohAllowedSPLH',
        header: 'FOH: Allowed SPLH',
        accessorKey: 'fohAllowedSPLH',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const isTotal = row.original.restaurant === 'TOTALS';

          const displayValue = value === 0 ? '+0' : `${value > 0 ? '+' : ''}${value}`;
          const textStyle = {
            fontWeight: '500',
            color: value === 0 ? 'inherit' : (value >= 0 ? '#2e7d32' : '#d32f2f')
          };

          return <div style={{ ...textStyle, textAlign: 'center' }}>{displayValue}</div>;
        },
      },
      {
        id: 'fohVarianceToAdjusted',
        header: 'FOH: Variance to Adjusted',
        accessorKey: 'fohVarianceToAdjusted',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const isTotal = row.original.restaurant === 'TOTALS';
          const actualHours = row.original.fohActualHours;
          const adjHours = row.original.fohAdjHours;

          const displayValue = value === 0 ? '+0' : `${value > 0 ? '+' : ''}${value}`;
          const textStyle = {
            fontWeight: '500',
            color: value === 0 ? 'inherit' : (value >= 0 ? '#2e7d32' : '#d32f2f')
          };

          // Show tooltip for restaurant rows and area rows (when Area Summary is NOT selected), but not totals
          if (!isTotal && (actualHours !== null && actualHours !== undefined) && (adjHours !== null && adjHours !== undefined) && selectedArea !== 'Area Summary') {
            return (
              <Tooltip
                title={
                  <div>
                    <div>Actual FOH: {(Number(actualHours) || 0).toFixed(2)}</div>
                    <div>Adj FOH: {(Number(adjHours) || 0).toFixed(2)}</div>
                  </div>
                }
                placement="top"
                arrow
              >
                <div style={{ ...textStyle, cursor: 'help', textAlign: 'center' }}>
                  {displayValue}
                </div>
              </Tooltip>
            );
          }

          return <div style={{ ...textStyle, textAlign: 'center' }}>{displayValue}</div>;
        },
      },
      {
        id: 'fohTrainingHours',
        header: 'Training Hours',
        accessorKey: 'fohTrainingHours',
        cell: ({ getValue }) => {
          const value = Math.round(getValue() || 0);
          return (
            <div style={{ fontWeight: '500', color: '#000000', textAlign: 'center' }}>
              {value}
            </div>
          );
        },
      },
      // BOH Columns
      {
        id: 'bohSchedVsModel',
        header: 'BOH: Sched vs Model',
        accessorKey: 'bohSchedVsModel',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const isTotal = row.original.restaurant === 'TOTALS';
          const schedHours = row.original.bohSchedHours;
          const theoHours = row.original.bohTheoHours;
          const trainingHours = row.original.bohTrainingHours;

          const displayValue = value === 0 ? '+0' : `${value > 0 ? '+' : ''}${value}`;
          const textStyle = {
            fontWeight: '500',
            color: value === 0 ? 'inherit' : (value >= 0 ? '#2e7d32' : '#d32f2f')
          };

          // Show tooltip for restaurant rows and area rows (when Area Summary is NOT selected), but not totals
          if (!isTotal && (schedHours !== null && schedHours !== undefined) && (theoHours !== null && theoHours !== undefined) && selectedArea !== 'Area Summary') {
            return (
              <Tooltip
                title={
                  <div>
                    <div>Sched BOH: {(Number(schedHours) || 0).toFixed(2)}</div>
                    <div>Training BOH: {(Number(trainingHours) || 0).toFixed(2)}</div>
                    <div>Theo BOH: {(Number(theoHours) || 0).toFixed(2)}</div>
                  </div>
                }
                placement="top"
                arrow
              >
                <div style={{ ...textStyle, cursor: 'help', textAlign: 'center' }}>
                  {displayValue}
                </div>
              </Tooltip>
            );
          }

          return <div style={{ ...textStyle, textAlign: 'center' }}>{displayValue}</div>;
        },
      },
      {
        id: 'bohChangeDuringDay',
        header: 'BOH: Change During Day',
        accessorKey: 'bohChangeDuringDay',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const isTotal = row.original.restaurant === 'TOTALS';
          const actualHours = row.original.bohActualHours;
          const schedHours = row.original.bohSchedHours;
          
          const displayValue = value === 0 ? '+0' : `${value > 0 ? '+' : ''}${value}`;
          const textStyle = {
            fontWeight: '500',
            color: value === 0 ? 'inherit' : (value >= 0 ? '#2e7d32' : '#d32f2f')
          };
          
          // Show tooltip for restaurant rows and area rows (when Area Summary is NOT selected), but not totals
          if (!isTotal && (actualHours !== null && actualHours !== undefined) && (schedHours !== null && schedHours !== undefined) && selectedArea !== 'Area Summary') {
            return (
              <Tooltip 
                title={
                  <div>
                    <div>Actual BOH: {(Number(actualHours) || 0).toFixed(2)}</div>
                    <div>Sched BOH: {(Number(schedHours) || 0).toFixed(2)}</div>
                  </div>
                }
                placement="top"
                arrow
              >
                <div style={{ ...textStyle, cursor: 'help', textAlign: 'center' }}>
                  {displayValue}
                </div>
              </Tooltip>
            );
          }
          
          return <div style={{ ...textStyle, textAlign: 'center' }}>{displayValue}</div>;
        },
      },
      {
        id: 'bohAllowedSPLH',
        header: 'BOH: Allowed SPLH',
        accessorKey: 'bohAllowedSPLH',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const isTotal = row.original.restaurant === 'TOTALS';

          const displayValue = value === 0 ? '+0' : `${value > 0 ? '+' : ''}${value}`;
          const textStyle = {
            fontWeight: '500',
            color: value === 0 ? 'inherit' : (value >= 0 ? '#2e7d32' : '#d32f2f')
          };

          return <div style={{ ...textStyle, textAlign: 'center' }}>{displayValue}</div>;
        },
      },
      {
        id: 'bohVarianceToAdjusted',
        header: 'BOH: Variance to Adjusted',
        accessorKey: 'bohVarianceToAdjusted',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const isTotal = row.original.restaurant === 'TOTALS';
          const actualHours = row.original.bohActualHours;
          const adjHours = row.original.bohAdjHours;

          const displayValue = value === 0 ? '+0' : `${value > 0 ? '+' : ''}${value}`;
          const textStyle = {
            fontWeight: '500',
            color: value === 0 ? 'inherit' : (value >= 0 ? '#2e7d32' : '#d32f2f')
          };

          // Show tooltip for restaurant rows and area rows (when Area Summary is NOT selected), but not totals
          if (!isTotal && (actualHours !== null && actualHours !== undefined) && (adjHours !== null && adjHours !== undefined) && selectedArea !== 'Area Summary') {
            return (
              <Tooltip
                title={
                  <div>
                    <div>Actual BOH: {(Number(actualHours) || 0).toFixed(2)}</div>
                    <div>Adj BOH: {(Number(adjHours) || 0).toFixed(2)}</div>
                  </div>
                }
                placement="top"
                arrow
              >
                <div style={{ ...textStyle, cursor: 'help', textAlign: 'center' }}>
                  {displayValue}
                </div>
              </Tooltip>
            );
          }

          return <div style={{ ...textStyle, textAlign: 'center' }}>{displayValue}</div>;
        },
      },
      {
        id: 'bohTrainingHours',
        header: 'Training Hours',
        accessorKey: 'bohTrainingHours',
        cell: ({ getValue }) => {
          const value = Math.round(getValue() || 0);
          return (
            <div style={{ fontWeight: '500', color: '#000000', textAlign: 'center' }}>
              {value}
            </div>
          );
        },
      },
    ],
    [selectedArea]
  );

  // Prepare table data
  const tableData = useMemo(() => {
    if (!data?.laborHours) return [];

    return data.laborHours.map(laborData => {
        // Calculate variance values
        const fohSchedVsModel = Math.round(laborData.foh.schedVsModel);
        const fohChangeDuringDay = Math.round(laborData.foh.changeDuringDay);
        const fohVarianceToAdjusted = Math.round(laborData.foh.varianceToAdjusted);
        const bohSchedVsModel = Math.round(laborData.boh.schedVsModel);
        const bohChangeDuringDay = Math.round(laborData.boh.changeDuringDay);
        const bohVarianceToAdjusted = Math.round(laborData.boh.varianceToAdjusted);

        return {
          restaurant: laborData.area,
          // FOH data
          fohSchedVsModel,
          fohChangeDuringDay,
          fohAllowedSPLH: Math.round(laborData.foh.allowedSPLH),
          fohVarianceToAdjusted,
          // FOH raw hours for tooltips
          fohSchedHours: Number(laborData.foh.schedHours) || 0,
          fohTheoHours: Number(laborData.foh.theoHours) || 0,
          fohActualHours: Number(laborData.foh.actualHours) || 0,
          fohAdjHours: Number(laborData.foh.adjHours) || 0,
          fohTrainingHours: Number(laborData.foh.trainingHours) || 0,
          // BOH data
          bohSchedVsModel,
          bohChangeDuringDay,
          bohAllowedSPLH: Math.round(laborData.boh.allowedSPLH),
          bohVarianceToAdjusted,
          // BOH raw hours for tooltips
          bohSchedHours: Number(laborData.boh.schedHours) || 0,
          bohTheoHours: Number(laborData.boh.theoHours) || 0,
          bohActualHours: Number(laborData.boh.actualHours) || 0,
          bohAdjHours: Number(laborData.boh.adjHours) || 0,
          bohTrainingHours: Number(laborData.boh.trainingHours) || 0,
        };
      });
  }, [data]);

  // Add totals row
  const tableDataWithTotals = useMemo(() => {
    if (tableData.length === 0) return [];

    // Calculate totals by summing all rows (allowedSPLH is already calculated per row)
    const totals = tableData.reduce(
      (acc, row) => ({
        restaurant: 'TOTALS',
        fohSchedVsModel: acc.fohSchedVsModel + row.fohSchedVsModel,
        fohChangeDuringDay: acc.fohChangeDuringDay + row.fohChangeDuringDay,
        fohAllowedSPLH: acc.fohAllowedSPLH + row.fohAllowedSPLH,
        fohVarianceToAdjusted: acc.fohVarianceToAdjusted + row.fohVarianceToAdjusted,
        fohTrainingHours: acc.fohTrainingHours + (row.fohTrainingHours || 0),
        bohSchedVsModel: acc.bohSchedVsModel + row.bohSchedVsModel,
        bohChangeDuringDay: acc.bohChangeDuringDay + row.bohChangeDuringDay,
        bohAllowedSPLH: acc.bohAllowedSPLH + row.bohAllowedSPLH,
        bohVarianceToAdjusted: acc.bohVarianceToAdjusted + row.bohVarianceToAdjusted,
        bohTrainingHours: acc.bohTrainingHours + (row.bohTrainingHours || 0),
      }),
      {
        restaurant: 'TOTALS',
        fohSchedVsModel: 0,
        fohChangeDuringDay: 0,
        fohAllowedSPLH: 0,
        fohVarianceToAdjusted: 0,
        fohTrainingHours: 0,
        bohSchedVsModel: 0,
        bohChangeDuringDay: 0,
        bohAllowedSPLH: 0,
        bohVarianceToAdjusted: 0,
        bohTrainingHours: 0,
      }
    );

    return [...tableData, totals];
  }, [tableData]);

  const table = useReactTable({
    data: tableDataWithTotals,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (!dataReady) {
    return null;
  }

  const tableStyles = {
    container: {
      marginBottom: '0.75rem',
    },
    card: {
      backgroundColor: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      borderRadius: '8px',
      padding: '16px',
      border: '1px solid #e0e0e0',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '12px',
    },
    icon: {
      marginRight: '8px',
      color: '#9c27b0',
      fontSize: '20px',
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#333333',
      margin: 0,
    },
    table: {
      width: '100%',
      minWidth: '700px',
      borderCollapse: 'collapse',
      fontSize: '13px',
    },
    th: {
      backgroundColor: 'transparent',
      padding: '4px 8px',
      textAlign: 'left',
      fontWeight: '500',
      borderBottom: '1px solid #e0e0e0',
      cursor: 'pointer',
      userSelect: 'none',
      fontSize: '13px',
      color: '#666666',
    },
    thGrouped: {
      backgroundColor: 'transparent',
      padding: '4px 12px',
      textAlign: 'center',
      fontWeight: '600',
      borderBottom: '1px solid #e0e0e0',
      fontSize: '14px',
    },
    thSub: {
      backgroundColor: 'transparent',
      padding: '4px 8px',
      textAlign: 'center',
      fontWeight: '500',
      borderBottom: '1px solid #e0e0e0',
      cursor: 'pointer',
      userSelect: 'none',
      fontSize: '12px',
      color: '#666666',
    },
    td: {
      padding: '4px 8px',
      borderBottom: '1px solid #f0f0f0',
      textAlign: 'center',
      fontSize: '13px',
    },
    tdRestaurant: {
      padding: '4px 8px',
      borderBottom: '1px solid #f0f0f0',
      textAlign: 'left',
      fontSize: '13px',
    },
    totalRow: {
      backgroundColor: '#f8f9fa',
      borderTop: '2px solid #e0e0e0',
      fontWeight: 'bold',
    },
    fohBorder: {
      borderRight: '2px solid #e0e0e0',
    },
  };

  // Check if labor hours data is available
  if (!data?.laborHours || data.laborHours.length === 0) {
    return (
      <div style={tableStyles.container}>
        <div style={tableStyles.card}>
          <div style={tableStyles.header}>
            <AccessTimeIcon sx={tableStyles.icon} />
            <h3 style={tableStyles.title}>Labor Hours Management</h3>
          </div>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="error" sx={{ mb: 2 }}>
              Labor Hours Data Unavailable
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {data?.laborError || 'No labor hours data available for the selected date range'}
            </Typography>
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div style={tableStyles.container}>
      {/* White Card Container */}
      <div style={tableStyles.card}>
        {/* Table Header */}
        <div style={tableStyles.header}>
          <AccessTimeIcon sx={tableStyles.icon} />
          <h3 style={tableStyles.title}>
            Labor Hours Management
            {currentView === 'all-stores' ? ' - All Stores' : ''}
          </h3>
        </div>

        {/* Custom Table with Grouped Headers */}
        <table style={tableStyles.table}>
          <thead>
            {/* Main Header Row */}
            <tr>
              <th
                rowSpan={2}
                style={{
                  ...tableStyles.th,
                  verticalAlign: 'bottom',
                  paddingBottom: '16px',
                }}
              >
                Restaurant
              </th>
              <th
                colSpan={5}
                style={{
                  ...tableStyles.thGrouped,
                  color: '#1976d2',
                }}
              >
                FOH Hours
              </th>
              <th
                colSpan={5}
                style={{
                  ...tableStyles.thGrouped,
                  color: '#9c27b0',
                }}
              >
                BOH Hours
              </th>
            </tr>
            
            {/* Sub Header Row */}
            <tr>
              {/* FOH Sub Headers */}
              <th
                style={tableStyles.thSub}
                onClick={() => table.getColumn('fohSchedVsModel')?.toggleSorting()}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Sched vs Model
                  {{
                    asc: ' 🔼',
                    desc: ' 🔽',
                  }[table.getColumn('fohSchedVsModel')?.getIsSorted()] ?? null}
                </div>
              </th>
              <th
                style={tableStyles.thSub}
                onClick={() => table.getColumn('fohChangeDuringDay')?.toggleSorting()}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Change During Day
                  {{
                    asc: ' 🔼',
                    desc: ' 🔽',
                  }[table.getColumn('fohChangeDuringDay')?.getIsSorted()] ?? null}
                </div>
              </th>
              <th
                style={tableStyles.thSub}
                onClick={() => table.getColumn('fohAllowedSPLH')?.toggleSorting()}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Allowed SPLH
                  {{
                    asc: ' 🔼',
                    desc: ' 🔽',
                  }[table.getColumn('fohAllowedSPLH')?.getIsSorted()] ?? null}
                </div>
              </th>
              <th
                style={tableStyles.thSub}
                onClick={() => table.getColumn('fohVarianceToAdjusted')?.toggleSorting()}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Variance to Adjusted
                  {{
                    asc: ' 🔼',
                    desc: ' 🔽',
                  }[table.getColumn('fohVarianceToAdjusted')?.getIsSorted()] ?? null}
                </div>
              </th>
              <th
                style={{
                  ...tableStyles.thSub,
                  ...tableStyles.fohBorder,
                }}
                onClick={() => table.getColumn('fohTrainingHours')?.toggleSorting()}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Training Hours
                  {{
                    asc: ' 🔼',
                    desc: ' 🔽',
                  }[table.getColumn('fohTrainingHours')?.getIsSorted()] ?? null}
                </div>
              </th>

              {/* BOH Sub Headers */}
              <th
                style={tableStyles.thSub}
                onClick={() => table.getColumn('bohSchedVsModel')?.toggleSorting()}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Sched vs Model
                  {{
                    asc: ' 🔼',
                    desc: ' 🔽',
                  }[table.getColumn('bohSchedVsModel')?.getIsSorted()] ?? null}
                </div>
              </th>
              <th
                style={tableStyles.thSub}
                onClick={() => table.getColumn('bohChangeDuringDay')?.toggleSorting()}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Change During Day
                  {{
                    asc: ' 🔼',
                    desc: ' 🔽',
                  }[table.getColumn('bohChangeDuringDay')?.getIsSorted()] ?? null}
                </div>
              </th>
              <th
                style={tableStyles.thSub}
                onClick={() => table.getColumn('bohAllowedSPLH')?.toggleSorting()}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Allowed SPLH
                  {{
                    asc: ' 🔼',
                    desc: ' 🔽',
                  }[table.getColumn('bohAllowedSPLH')?.getIsSorted()] ?? null}
                </div>
              </th>
              <th
                style={tableStyles.thSub}
                onClick={() => table.getColumn('bohVarianceToAdjusted')?.toggleSorting()}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Variance to Adjusted
                  {{
                    asc: ' 🔼',
                    desc: ' 🔽',
                  }[table.getColumn('bohVarianceToAdjusted')?.getIsSorted()] ?? null}
                </div>
              </th>
              <th
                style={tableStyles.thSub}
                onClick={() => table.getColumn('bohTrainingHours')?.toggleSorting()}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Training Hours
                  {{
                    asc: ' 🔼',
                    desc: ' 🔽',
                  }[table.getColumn('bohTrainingHours')?.getIsSorted()] ?? null}
                </div>
              </th>
            </tr>
          </thead>
          
          <tbody>
            {table.getRowModel().rows.map((row, index) => {
              const isTotal = row.original.restaurant === 'TOTALS';
              
              return (
                <tr
                  key={row.id}
                  style={{
                    ...(isTotal ? tableStyles.totalRow : {}),
                  }}
                  onMouseEnter={(e) => {
                    if (!isTotal) {
                      e.currentTarget.style.backgroundColor = '#fafafa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isTotal) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const isRestaurantCell = cellIndex === 0;
                    const isFohLastCell = cellIndex === 5;
                    
                    return (
                      <td
                        key={cell.id}
                        style={{
                          ...(isRestaurantCell ? tableStyles.tdRestaurant : tableStyles.td),
                          fontWeight: isTotal ? 'bold' : 'normal',
                          ...(isFohLastCell ? tableStyles.fohBorder : {}),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}